import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings as SettingsIcon, Plus, Trash2, RotateCcw, Save, AlertCircle, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import { BILLING_PRESETS, DEFAULT_BILLING_FORMULA, validateBillingFormula, evalBillingFormula } from '../utils/billingFormula';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import {
  DEFAULT_MACHINE_STATUS_CONFIG,
  MachineStatusConfig,
  EfficiencyRange,
} from '../constants/machineStatusDefaults';
import {
  DEFAULT_REPORT_STYLE_CONFIG,
  ReportColumnConfig,
  ReportStyleConfig,
  StopRowColorEntry,
} from '../constants/reportStyleDefaults';

// A saved report config keeps the column list from when it was saved. Add any
// column introduced since (e.g. 'offlineTime') at its default spot, so admins can
// still toggle it. Saved order and visibility are kept as they are.
function withNewDefaultColumns(saved?: ReportColumnConfig[] | null): ReportColumnConfig[] {
  const defaults = DEFAULT_REPORT_STYLE_CONFIG.visibleColumns;
  if (!Array.isArray(saved) || saved.length === 0) return defaults.map((c) => ({ ...c }));
  const result = saved.map((c) => ({ ...c }));
  defaults.forEach((def, i) => {
    if (result.some((c) => c.key === def.key)) return;
    // Insert after the nearest earlier default column that the saved list has.
    let insertAt = result.length;
    for (let j = i - 1; j >= 0; j--) {
      const idx = result.findIndex((c) => c.key === defaults[j].key);
      if (idx !== -1) { insertAt = idx + 1; break; }
    }
    result.splice(insertAt, 0, { ...def });
  });
  return result;
}

// Helper to convert rgb() strings to hex for the color picker
function rgbToHex(rgb: string): string {
  const match = rgb.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
  if (!match) return rgb;
  const r = parseInt(match[1], 10);
  const g = parseInt(match[2], 10);
  const b = parseInt(match[3], 10);
  return '#' + [r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('');
}

// Reusable color input component
const ColorInput: React.FC<{
  label: string;
  value: string;
  onChange: (val: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="flex items-center gap-2">
    <label className="text-xs text-gray-600 w-28 flex-shrink-0">{label}</label>
    <div
      className="w-6 h-6 rounded border border-gray-300 flex-shrink-0"
      style={{ backgroundColor: value }}
    />
    <input
      type="color"
      value={value.startsWith('rgb') ? rgbToHex(value) : value}
      onChange={(e) => onChange(e.target.value)}
      className="w-6 h-6 rounded cursor-pointer border-0 flex-shrink-0"
    />
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-28 px-2 py-1 text-xs border border-gray-300 rounded-md"
    />
  </div>
);

// Reusable font size input component
const SizeInput: React.FC<{
  label: string;
  value: string;
  onChange: (val: string) => void;
}> = ({ label, value, onChange }) => (
  <div className="flex items-center gap-2">
    <label className="text-xs text-gray-600 w-28 flex-shrink-0">{label}</label>
    <input
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-24 px-2 py-1 text-xs border border-gray-300 rounded-md"
      placeholder="e.g. 6.5px"
    />
  </div>
);

// Collapsible section component
const CollapsibleSection: React.FC<{
  title: string;
  description?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}> = ({ title, description, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 text-left"
      >
        {open ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-500" />}
        <div>
          <span className="text-sm font-medium text-gray-900">{title}</span>
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
      </button>
      {open && <div className="p-4 space-y-3">{children}</div>}
    </div>
  );
};

// Scale a CSS size value for the preview (e.g. "6.5px" → "13px" at 2x)
function scaleSize(cssSize: string, factor: number): string {
  const match = cssSize.match(/^(\d+(?:\.\d+)?)(px|pt|em|rem)$/);
  if (!match) return cssSize;
  const scaled = parseFloat(match[1]) * factor;
  return `${Math.round(scaled * 10) / 10}${match[2]}`;
}

// Bump a CSS size by a number of units (e.g. "6.5px" + 1 → "7.5px")
function bumpSize(cssSize: string, delta: number): string {
  const match = cssSize.match(/^(\d+(?:\.\d+)?)(px|pt|em|rem)$/);
  if (!match) return cssSize;
  const bumped = parseFloat(match[1]) + delta;
  return `${Math.round(bumped * 10) / 10}${match[2]}`;
}


// Generate preview HTML for the report — uses scaled-up sizes for readability
function generatePreviewHtml(config: ReportStyleConfig): string {
  const s = config;
  const f = 2; // scale factor for font sizes
  return `<!DOCTYPE html>
<html><head><style>
  * { box-sizing: border-box; }
  body { font-family: ${s.body.fontFamily}; font-size: ${scaleSize(s.body.fontSize, f)}; color: ${s.body.textColor}; margin: 0; padding: 16px; background: white; }
  .header { background: ${s.header.backgroundColor}; color: white; padding: 12px 16px; text-align: center; border-radius: 4px 4px 0 0; }
  .header h2 { margin: 0; font-size: ${scaleSize(s.header.titleFontSize, f)}; }
  .header p { margin: 4px 0 0; font-size: ${scaleSize(s.header.subtitleFontSize, f)}; opacity: 0.9; }
  .line-section { margin-top: 12px; border: 1px solid ${s.lineSection.borderColor}; border-radius: 4px; overflow: hidden; }
  .line-header { background: ${s.lineSection.headerBackground}; display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; border-bottom: 1px solid ${s.lineSection.borderColor}; font-size: ${scaleSize(s.lineSection.headerFontSize, f)}; }
  .line-header b { font-weight: normal; font-size: ${scaleSize(bumpSize(s.lineSection.headerFontSize, 1), f)}; }
  table { width: 100%; border-collapse: collapse; }
  th { background: ${s.tableHeader.backgroundColor}; color: white; padding: 6px 8px; border: 1px solid ${s.tableHeader.borderColor}; font-size: ${scaleSize(s.tableHeader.fontSize, f)}; text-transform: uppercase; white-space: nowrap; }
  td { padding: 6px 8px; border: 1px solid ${s.tableCells.borderColor}; text-align: right; font-size: ${scaleSize(s.tableCells.fontSize, f)}; white-space: nowrap; }
  .m-no { text-align: center; font-size: ${scaleSize(bumpSize(s.tableCells.fontSize, 1), f)}; }
  .day-cell { }
  .ngt-cell { }
  .tot-cell { font-size: ${scaleSize(bumpSize(s.tableCells.fontSize, 1), f)}; }
  .low-eff { color: ${s.tableCells.lowEfficiencyColor}; font-size: ${scaleSize(bumpSize(s.tableCells.fontSize, 1), f)}; }
  .summary-section { margin-top: 12px; border: 2px solid ${s.summary.borderColor}; border-radius: 4px; overflow: hidden; }
  .summary-title { background: ${s.summary.titleBackground}; color: white; padding: 8px 12px; font-size: ${scaleSize(s.summary.titleFontSize, f)}; }
  .summary-content { display: flex; justify-content: space-around; flex-wrap: wrap; padding: 12px; font-size: ${scaleSize(s.summary.contentFontSize, f)}; gap: 8px; }
  .summary-value { font-size: ${scaleSize(s.summary.valueFontSize, f)}; }
  .stop-legend { margin-top: 12px; display: flex; gap: 8px; flex-wrap: wrap; }
  .stop-chip { padding: 4px 10px; border-radius: 4px; color: white; font-size: 12px; }
</style></head><body>
  <div class="header">
    <h2>FULL PERFORMANCE REPORT</h2>
    <p>Period: 26/02/26 08:00 - 27/02/26 08:00</p>
  </div>
  ${(() => {
    const cv: Record<string, boolean> = {};
    (s.visibleColumns || []).forEach((c: any) => { cv[c.key] = c.visible; });
    const sh = (k: string) => cv[k] !== false;
    return `<div class="line-section">
    <div class="line-header">
      <span>Line-1</span>
      <div>
        <span style="margin-right:12px">Day: <b>4520m</b> (92%)</span>
        <span style="margin-right:12px">Night: <b>3980m</b> (88%)</span>
        <span>Total: <b>8500m</b> (90%)</span>
      </div>
    </div>
    <table>
      <thead><tr>
        <th>M#</th>
        ${sh('rpm') ? '<th>RPM</th>' : ''}
        ${sh('pick') ? '<th>Pick</th>' : ''}
        ${sh('dayMeter') ? '<th class="day-cell">D.Mtr</th>' : ''}
        ${sh('dayEff') ? '<th class="day-cell">D.Eff</th>' : ''}
        ${sh('nightMeter') ? '<th class="ngt-cell">N.Mtr</th>' : ''}
        ${sh('nightEff') ? '<th class="ngt-cell">N.Eff</th>' : ''}
        ${sh('totalMeter') ? '<th class="tot-cell">T.Mtr</th>' : ''}
        ${sh('totalEff') ? '<th class="tot-cell">T.Eff</th>' : ''}
        ${sh('stopCount') ? '<th>Stp</th>' : ''}
        ${sh('runTime') ? '<th>Run</th>' : ''}
        ${sh('stopTime') ? '<th>Stop</th>' : ''}
        ${sh('offlineTime') ? '<th>Offline</th>' : ''}
      </tr></thead>
      <tbody>
        <tr>
          <td class="m-no">1</td>
          ${sh('rpm') ? '<td>450</td>' : ''}
          ${sh('pick') ? '<td>60</td>' : ''}
          ${sh('dayMeter') ? '<td class="day-cell">520</td>' : ''}
          ${sh('dayEff') ? '<td class="day-cell">96%</td>' : ''}
          ${sh('nightMeter') ? '<td class="ngt-cell">480</td>' : ''}
          ${sh('nightEff') ? '<td class="ngt-cell">94%</td>' : ''}
          ${sh('totalMeter') ? '<td class="tot-cell">1000</td>' : ''}
          ${sh('totalEff') ? '<td class="tot-cell">95%</td>' : ''}
          ${sh('stopCount') ? '<td>2</td>' : ''}
          ${sh('runTime') ? '<td>22h 30m</td>' : ''}
          ${sh('stopTime') ? '<td>1h 15m</td>' : ''}
          ${sh('offlineTime') ? '<td>0h 15m</td>' : ''}
        </tr>
        <tr>
          <td class="m-no">2</td>
          ${sh('rpm') ? '<td>440</td>' : ''}
          ${sh('pick') ? '<td>58</td>' : ''}
          ${sh('dayMeter') ? '<td class="day-cell">490</td>' : ''}
          ${sh('dayEff') ? '<td class="day-cell">92%</td>' : ''}
          ${sh('nightMeter') ? '<td class="ngt-cell">460</td>' : ''}
          ${sh('nightEff') ? '<td class="ngt-cell">90%</td>' : ''}
          ${sh('totalMeter') ? '<td class="tot-cell">950</td>' : ''}
          ${sh('totalEff') ? '<td class="tot-cell">91%</td>' : ''}
          ${sh('stopCount') ? '<td>3</td>' : ''}
          ${sh('runTime') ? '<td>21h 15m</td>' : ''}
          ${sh('stopTime') ? '<td>2h 15m</td>' : ''}
          ${sh('offlineTime') ? '<td>0h 30m</td>' : ''}
        </tr>
        <tr>
          <td class="m-no">3</td>
          ${sh('rpm') ? '<td>420</td>' : ''}
          ${sh('pick') ? '<td>55</td>' : ''}
          ${sh('dayMeter') ? '<td class="day-cell">380</td>' : ''}
          ${sh('dayEff') ? '<td class="day-cell">78%</td>' : ''}
          ${sh('nightMeter') ? '<td class="ngt-cell">350</td>' : ''}
          ${sh('nightEff') ? '<td class="ngt-cell">72%</td>' : ''}
          ${sh('totalMeter') ? '<td class="tot-cell">730</td>' : ''}
          ${sh('totalEff') ? '<td class="tot-cell low-eff">75%</td>' : ''}
          ${sh('stopCount') ? '<td>5</td>' : ''}
          ${sh('runTime') ? '<td>18h 0m</td>' : ''}
          ${sh('stopTime') ? '<td>5h 0m</td>' : ''}
          ${sh('offlineTime') ? '<td>1h 0m</td>' : ''}
        </tr>
      </tbody>
    </table>
  </div>`;
  })()}
  <div class="summary-section">
    <div class="summary-title">OVERALL PERFORMANCE SUMMARY</div>
    <div class="summary-content">
      <div>Total Machines: <span class="summary-value">3</span></div>
      <div>Day Meters: <span class="summary-value">1390 m</span></div>
      <div>Night Meters: <span class="summary-value">1290 m</span></div>
      <div>Total Meters: <span class="summary-value">2680 m</span></div>
    </div>
    <div class="summary-content">
      <div>Shift Hrs <span class="summary-value">24h</span></div>
      <div>Running <span class="summary-value">20h 35m</span></div>
      <div>Stopped <span class="summary-value">2h 50m</span></div>
      <div>Offline <span class="summary-value">0h 35m</span></div>
      <div>Avg Eff <span class="summary-value">87%</span></div>
    </div>
  </div>
  <div class="stop-legend">
    ${s.stopRowColors.map(c => `<span class="stop-chip" style="background:${c.color}">${c.label}</span>`).join('')}
  </div>
</body></html>`;
}

const Settings: React.FC = () => {
  const queryClient = useQueryClient();

  const { data: settingsData, isLoading } = useQuery({
    queryKey: ['admin-settings'],
    queryFn: async () => apiService.get('/admin-settings'),
    staleTime: 5 * 60 * 1000,
  });

  // Machine status config state
  const [localConfig, setLocalConfig] = useState<MachineStatusConfig>(DEFAULT_MACHINE_STATUS_CONFIG);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  // Report style config state
  const [reportStyle, setReportStyle] = useState<ReportStyleConfig>(
    JSON.parse(JSON.stringify(DEFAULT_REPORT_STYLE_CONFIG))
  );
  const [reportStyleErrors, setReportStyleErrors] = useState<string[]>([]);

  // Billing formula (company-wide challan/bill amount rule). `billingCustom` = free-form vs a preset.
  const [billingFormula, setBillingFormula] = useState<string>(DEFAULT_BILLING_FORMULA);
  const [billingCustom, setBillingCustom] = useState<boolean>(false);

  useEffect(() => {
    const serverConfig = (settingsData as any)?.settings?.machineStatusConfig;
    if (serverConfig) {
      setLocalConfig(serverConfig);
    }
    const serverReportStyle = (settingsData as any)?.settings?.reportStyleConfig;
    if (serverReportStyle) {
      setReportStyle({
        ...DEFAULT_REPORT_STYLE_CONFIG,
        ...serverReportStyle,
        visibleColumns: withNewDefaultColumns(serverReportStyle.visibleColumns),
      });
    }
    const serverFormula = (settingsData as any)?.settings?.billingFormula;
    if (serverFormula) {
      setBillingFormula(serverFormula);
      setBillingCustom(!BILLING_PRESETS.some((p) => p.formula === serverFormula));
    }
  }, [settingsData]);

  // Machine status mutations
  const saveMutation = useMutation({
    mutationFn: async (config: MachineStatusConfig) => {
      return apiService.put('/admin-settings', { machineStatusConfig: config });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Machine status config saved successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to save config');
    },
  });

  // Report style mutations
  const saveReportStyleMutation = useMutation({
    mutationFn: async (config: ReportStyleConfig) => {
      return apiService.put('/admin-settings', { reportStyleConfig: config });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Report style saved successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to save report style');
    },
  });

  // Billing formula mutation
  const saveBillingMutation = useMutation({
    mutationFn: async (formula: string) => apiService.put('/admin-settings', { billingFormula: formula }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-settings'] });
      toast.success('Billing formula saved');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to save billing formula');
    },
  });

  const validate = (cfg: MachineStatusConfig): string[] => {
    const errors: string[] = [];

    if (cfg.efficiencyRanges.length === 0) {
      errors.push('At least one efficiency range is required');
      return errors;
    }

    for (let i = 0; i < cfg.efficiencyRanges.length; i++) {
      const r = cfg.efficiencyRanges[i];
      if (r.min >= r.max) errors.push(`Range ${i + 1}: min must be less than max`);
      if (!r.color) errors.push(`Range ${i + 1}: color is required`);
      if (!r.label) errors.push(`Range ${i + 1}: label is required`);
      if (r.label.length > 5) errors.push(`Range ${i + 1}: label max 5 characters`);
      if (!r.statusText) errors.push(`Range ${i + 1}: status text is required`);
    }

    const sorted = [...cfg.efficiencyRanges].sort((a, b) => a.min - b.min);
    if (sorted[0].min !== 0) errors.push('Ranges must start from 0');
    if (sorted[sorted.length - 1].max !== 100) errors.push('Ranges must end at 100');
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].min !== sorted[i - 1].max) {
        errors.push(`Gap or overlap between ${sorted[i - 1].max} and ${sorted[i].min}`);
      }
    }

    for (const key of ['fault', 'manual', 'offline', 'stopped'] as const) {
      const s = cfg.specialStatuses[key];
      if (!s.color) errors.push(`${key}: color is required`);
      if (!s.label) errors.push(`${key}: label is required`);
      if (s.label.length > 5) errors.push(`${key}: label max 5 characters`);
      if (!s.statusText) errors.push(`${key}: status text is required`);
    }

    return errors;
  };

  const validateReportStyle = (cfg: ReportStyleConfig): string[] => {
    const errors: string[] = [];
    const cssSize = /^\d+(\.\d+)?(px|pt|em|rem)$/;

    if (!cfg.body.fontFamily) errors.push('Body font family is required');
    if (!cssSize.test(cfg.body.fontSize)) errors.push('Body font size must be valid (e.g. 6.5px)');
    if (!cfg.body.textColor) errors.push('Body text color is required');

    if (!cssSize.test(cfg.header.titleFontSize)) errors.push('Header title font size must be valid');
    if (!cssSize.test(cfg.header.subtitleFontSize)) errors.push('Header subtitle font size must be valid');

    if (!cssSize.test(cfg.lineSection.headerFontSize)) errors.push('Line header font size must be valid');
    if (!cssSize.test(cfg.tableHeader.fontSize)) errors.push('Table header font size must be valid');
    if (!cssSize.test(cfg.tableCells.fontSize)) errors.push('Table cell font size must be valid');

    if (!cssSize.test(cfg.summary.titleFontSize)) errors.push('Summary title font size must be valid');
    if (!cssSize.test(cfg.summary.contentFontSize)) errors.push('Summary content font size must be valid');
    if (!cssSize.test(cfg.summary.valueFontSize)) errors.push('Summary value font size must be valid');

    if (cfg.stopRowColors.length === 0) errors.push('At least one stop row color is required');
    for (let i = 0; i < cfg.stopRowColors.length; i++) {
      const c = cfg.stopRowColors[i];
      if (typeof c.minHours !== 'number' || c.minHours < 0) errors.push(`Stop color ${i + 1}: minHours must be >= 0`);
      if (!c.color) errors.push(`Stop color ${i + 1}: color is required`);
      if (!c.label) errors.push(`Stop color ${i + 1}: label is required`);
    }

    return errors;
  };

  const handleSave = () => {
    const errors = validate(localConfig);
    setValidationErrors(errors);
    if (errors.length > 0) return;
    saveMutation.mutate(localConfig);
  };

  const handleReset = () => {
    setLocalConfig(JSON.parse(JSON.stringify(DEFAULT_MACHINE_STATUS_CONFIG)));
    setValidationErrors([]);
  };

  const handleSaveReportStyle = () => {
    const errors = validateReportStyle(reportStyle);
    setReportStyleErrors(errors);
    if (errors.length > 0) return;
    saveReportStyleMutation.mutate(reportStyle);
  };

  const handleResetReportStyle = () => {
    setReportStyle(JSON.parse(JSON.stringify(DEFAULT_REPORT_STYLE_CONFIG)));
    setReportStyleErrors([]);
  };

  const updateRange = (index: number, field: keyof EfficiencyRange, value: any) => {
    setLocalConfig((prev) => {
      const ranges = [...prev.efficiencyRanges];
      ranges[index] = { ...ranges[index], [field]: field === 'min' || field === 'max' ? Number(value) : value };
      return { ...prev, efficiencyRanges: ranges };
    });
  };

  const addRange = () => {
    setLocalConfig((prev) => {
      const ranges = [...prev.efficiencyRanges];
      ranges.push({ min: 0, max: 0, color: '#888888', label: 'NEW', statusText: 'New Status' });
      return { ...prev, efficiencyRanges: ranges };
    });
  };

  const removeRange = (index: number) => {
    setLocalConfig((prev) => {
      const ranges = prev.efficiencyRanges.filter((_, i) => i !== index);
      return { ...prev, efficiencyRanges: ranges };
    });
  };

  const updateSpecial = (key: 'fault' | 'manual' | 'offline' | 'stopped', field: string, value: string) => {
    setLocalConfig((prev) => ({
      ...prev,
      specialStatuses: {
        ...prev.specialStatuses,
        [key]: { ...prev.specialStatuses[key], [field]: value },
      },
    }));
  };

  // Report style helpers
  const updateStyleField = (section: keyof Omit<ReportStyleConfig, 'stopRowColors'>, field: string, value: string) => {
    setReportStyle((prev) => ({
      ...prev,
      [section]: { ...(prev[section] as unknown as Record<string, string>), [field]: value },
    }));
  };

  const updateStopRowColor = (index: number, field: keyof StopRowColorEntry, value: any) => {
    setReportStyle((prev) => {
      const colors = [...prev.stopRowColors];
      colors[index] = { ...colors[index], [field]: field === 'minHours' ? Number(value) : value };
      return { ...prev, stopRowColors: colors };
    });
  };

  const addStopRowColor = () => {
    setReportStyle((prev) => ({
      ...prev,
      stopRowColors: [...prev.stopRowColors, { minHours: 0, color: '#888888', label: 'New' }],
    }));
  };

  const removeStopRowColor = (index: number) => {
    setReportStyle((prev) => ({
      ...prev,
      stopRowColors: prev.stopRowColors.filter((_, i) => i !== index),
    }));
  };

  const toggleColumnVisibility = (key: string) => {
    if (key === 'machineNo') return; // always visible
    setReportStyle((prev) => ({
      ...prev,
      visibleColumns: prev.visibleColumns.map((col) =>
        col.key === key ? { ...col, visible: !col.visible } : col
      ),
    }));
  };

  const previewHtml = useMemo(() => generatePreviewHtml(reportStyle), [reportStyle]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    );
  }

  const sortedRanges = [...localConfig.efficiencyRanges].sort((a, b) => b.min - a.min);

  // Live validation + preview for the billing formula (sample line: rate 10, meter 100, avgPick 48, piece 1)
  const billingError = validateBillingFormula(billingFormula);
  const billingPreview = evalBillingFormula(billingFormula, { rate: 10, meter: 100, avgPick: 48, piece: 1 });

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <SettingsIcon className="w-6 h-6 text-gray-700" />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Machine Status Configuration</h1>
            <p className="text-sm text-gray-500 mt-1">Configure efficiency ranges, colors, and labels for machine statuses</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleReset}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RotateCcw className="w-4 h-4" />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saveMutation.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saveMutation.isPending ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-red-600" />
            <span className="font-medium text-red-800">Validation Errors</span>
          </div>
          <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
            {validationErrors.map((err, i) => (
              <li key={i}>{err}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Efficiency Ranges */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Efficiency Ranges</h2>
            <p className="text-sm text-gray-500">Define efficiency ranges from 0% to 100%. Ranges must not overlap and must cover the full range.</p>
          </div>
          <button
            onClick={addRange}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
          >
            <Plus className="w-4 h-4" />
            Add Range
          </button>
        </div>
        <div className="p-6 space-y-3">
          {localConfig.efficiencyRanges.map((range, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              {/* Color preview */}
              <div
                className="w-8 h-8 rounded-md border border-gray-300 flex-shrink-0"
                style={{ backgroundColor: range.color }}
              />
              {/* Color input */}
              <div className="flex items-center gap-1">
                <input
                  type="color"
                  value={range.color.startsWith('rgb') ? rgbToHex(range.color) : range.color}
                  onChange={(e) => updateRange(index, 'color', e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border-0"
                />
                <input
                  type="text"
                  value={range.color}
                  onChange={(e) => updateRange(index, 'color', e.target.value)}
                  className="w-36 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  placeholder="Color"
                />
              </div>
              {/* Min / Max */}
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  value={range.min}
                  onChange={(e) => updateRange(index, 'min', e.target.value)}
                  className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded-md text-center"
                  min={0}
                  max={100}
                />
                <span className="text-gray-500 text-sm">-</span>
                <input
                  type="number"
                  value={range.max}
                  onChange={(e) => updateRange(index, 'max', e.target.value)}
                  className="w-16 px-2 py-1.5 text-sm border border-gray-300 rounded-md text-center"
                  min={0}
                  max={100}
                />
                <span className="text-gray-400 text-xs">%</span>
              </div>
              {/* Label */}
              <input
                type="text"
                value={range.label}
                onChange={(e) => updateRange(index, 'label', e.target.value)}
                maxLength={5}
                className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono font-bold uppercase text-center"
                placeholder="Label"
              />
              {/* Status Text */}
              <input
                type="text"
                value={range.statusText}
                onChange={(e) => updateRange(index, 'statusText', e.target.value)}
                className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                placeholder="Status text"
              />
              {/* Delete */}
              <button
                onClick={() => removeRange(index)}
                className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Special Statuses */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Special Statuses</h2>
          <p className="text-sm text-gray-500">Configure colors and labels for non-efficiency machine states.</p>
        </div>
        <div className="p-6 space-y-3">
          {(['fault', 'manual', 'offline', 'stopped'] as const).map((key) => {
            const status = localConfig.specialStatuses[key];
            return (
              <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                {/* Color preview */}
                <div
                  className="w-8 h-8 rounded-md border border-gray-300 flex-shrink-0"
                  style={{ backgroundColor: status.color }}
                />
                {/* Color input */}
                <div className="flex items-center gap-1">
                  <input
                    type="color"
                    value={status.color.startsWith('rgb') ? rgbToHex(status.color) : status.color}
                    onChange={(e) => updateSpecial(key, 'color', e.target.value)}
                    className="w-8 h-8 rounded cursor-pointer border-0"
                  />
                  <input
                    type="text"
                    value={status.color}
                    onChange={(e) => updateSpecial(key, 'color', e.target.value)}
                    className="w-36 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                    placeholder="Color"
                  />
                </div>
                {/* Key label */}
                <span className="w-16 text-sm font-medium text-gray-600 capitalize">{key}</span>
                {/* Label */}
                <input
                  type="text"
                  value={status.label}
                  onChange={(e) => updateSpecial(key, 'label', e.target.value)}
                  maxLength={5}
                  className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-md font-mono font-bold uppercase text-center"
                  placeholder="Label"
                />
                {/* Status Text */}
                <input
                  type="text"
                  value={status.statusText}
                  onChange={(e) => updateSpecial(key, 'statusText', e.target.value)}
                  className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-md"
                  placeholder="Status text"
                />
              </div>
            );
          })}
        </div>
      </div>

      {/* Stopped-card highlight — available to everyone who can open Settings */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Stopped Machine Highlight</h2>
          <p className="text-sm text-gray-500">
            When on, machine cards that are not running (stopped, fault, offline or manual) blink and
            highlight on the machines page and show how long they've been stopped. Save to apply.
          </p>
        </div>
        <div className="p-6">
          <label className="flex items-center justify-between gap-4 cursor-pointer">
            <span className="text-sm font-medium text-gray-700">
              Blink &amp; highlight stopped cards
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={!!localConfig.highlightStoppedCards}
              onClick={() =>
                setLocalConfig((prev) => ({ ...prev, highlightStoppedCards: !prev.highlightStoppedCards }))
              }
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                localConfig.highlightStoppedCards ? 'bg-red-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  localConfig.highlightStoppedCards ? 'translate-x-5' : 'translate-x-0.5'
                }`}
              />
            </button>
          </label>
        </div>
      </div>

      {/* Preview Panel */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Preview</h2>
          <p className="text-sm text-gray-500">See how your configured statuses will appear on machine cards.</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {/* Efficiency ranges preview */}
            {sortedRanges.map((range, i) => (
              <div key={`range-${i}`} className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <div className="h-2" style={{ backgroundColor: range.color }} />
                <div className="p-3 text-center">
                  <div
                    className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white mb-2"
                    style={{ backgroundColor: range.color }}
                  >
                    {range.label}
                  </div>
                  <div className="text-sm font-medium text-gray-900">{range.statusText}</div>
                  <div className="text-xs text-gray-500 mt-1">{range.min}% - {range.max}%</div>
                </div>
              </div>
            ))}
            {/* Special statuses preview */}
            {(['fault', 'manual', 'offline', 'stopped'] as const).map((key) => {
              const s = localConfig.specialStatuses[key];
              return (
                <div key={key} className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  <div className="h-2" style={{ backgroundColor: s.color }} />
                  <div className="p-3 text-center">
                    <div
                      className="inline-block px-2 py-0.5 rounded text-xs font-bold text-white mb-2"
                      style={{ backgroundColor: s.color }}
                    >
                      {s.label}
                    </div>
                    <div className="text-sm font-medium text-gray-900">{s.statusText}</div>
                    <div className="text-xs text-gray-500 mt-1 capitalize">{key}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* REPORT STYLING CONFIGURATION                                   */}
      {/* ═══════════════════════════════════════════════════════════════ */}

      <div className="border-t border-gray-300 pt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <FileText className="w-6 h-6 text-gray-700" />
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Report Styling</h1>
              <p className="text-sm text-gray-500 mt-1">Customize colors, font sizes, and appearance of the PDF performance reports</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleResetReportStyle}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RotateCcw className="w-4 h-4" />
              Reset to Defaults
            </button>
            <button
              onClick={handleSaveReportStyle}
              disabled={saveReportStyleMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saveReportStyleMutation.isPending ? 'Saving...' : 'Save Report Style'}
            </button>
          </div>
        </div>

        {/* Report Style Validation Errors */}
        {reportStyleErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="font-medium text-red-800">Report Style Validation Errors</span>
            </div>
            <ul className="list-disc list-inside text-sm text-red-700 space-y-1">
              {reportStyleErrors.map((err, i) => (
                <li key={i}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Report Style Form Sections */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-6 space-y-3">
            <CollapsibleSection title="Body Style" description="Base font family, size, and text color" defaultOpen>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-600 w-28 flex-shrink-0">Font Family</label>
                <input
                  type="text"
                  value={reportStyle.body.fontFamily}
                  onChange={(e) => updateStyleField('body', 'fontFamily', e.target.value)}
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md"
                />
              </div>
              <SizeInput label="Font Size" value={reportStyle.body.fontSize} onChange={(v) => updateStyleField('body', 'fontSize', v)} />
              <ColorInput label="Text Color" value={reportStyle.body.textColor} onChange={(v) => updateStyleField('body', 'textColor', v)} />
            </CollapsibleSection>

            <CollapsibleSection title="Header Style" description="Report header background and font sizes">
              <ColorInput label="Background" value={reportStyle.header.backgroundColor} onChange={(v) => updateStyleField('header', 'backgroundColor', v)} />
              <SizeInput label="Title Font Size" value={reportStyle.header.titleFontSize} onChange={(v) => updateStyleField('header', 'titleFontSize', v)} />
              <SizeInput label="Subtitle Font Size" value={reportStyle.header.subtitleFontSize} onChange={(v) => updateStyleField('header', 'subtitleFontSize', v)} />
            </CollapsibleSection>

            <CollapsibleSection title="Line Section" description="Line grouping borders and header">
              <ColorInput label="Border Color" value={reportStyle.lineSection.borderColor} onChange={(v) => updateStyleField('lineSection', 'borderColor', v)} />
              <ColorInput label="Header Background" value={reportStyle.lineSection.headerBackground} onChange={(v) => updateStyleField('lineSection', 'headerBackground', v)} />
              <SizeInput label="Header Font Size" value={reportStyle.lineSection.headerFontSize} onChange={(v) => updateStyleField('lineSection', 'headerFontSize', v)} />
            </CollapsibleSection>

            <CollapsibleSection title="Table Header" description="Column header background and font">
              <ColorInput label="Background" value={reportStyle.tableHeader.backgroundColor} onChange={(v) => updateStyleField('tableHeader', 'backgroundColor', v)} />
              <SizeInput label="Font Size" value={reportStyle.tableHeader.fontSize} onChange={(v) => updateStyleField('tableHeader', 'fontSize', v)} />
              <ColorInput label="Border Color" value={reportStyle.tableHeader.borderColor} onChange={(v) => updateStyleField('tableHeader', 'borderColor', v)} />
            </CollapsibleSection>

            <CollapsibleSection title="Table Cells" description="Data cell font, borders, and shift column colors">
              <SizeInput label="Font Size" value={reportStyle.tableCells.fontSize} onChange={(v) => updateStyleField('tableCells', 'fontSize', v)} />
              <ColorInput label="Border Color" value={reportStyle.tableCells.borderColor} onChange={(v) => updateStyleField('tableCells', 'borderColor', v)} />
              <ColorInput label="Machine # Bg" value={reportStyle.tableCells.machineNumberBg} onChange={(v) => updateStyleField('tableCells', 'machineNumberBg', v)} />
              <ColorInput label="Day Cell Bg" value={reportStyle.tableCells.dayCellBg} onChange={(v) => updateStyleField('tableCells', 'dayCellBg', v)} />
              <ColorInput label="Night Cell Bg" value={reportStyle.tableCells.nightCellBg} onChange={(v) => updateStyleField('tableCells', 'nightCellBg', v)} />
              <ColorInput label="Total Cell Bg" value={reportStyle.tableCells.totalCellBg} onChange={(v) => updateStyleField('tableCells', 'totalCellBg', v)} />
              <ColorInput label="Low Efficiency" value={reportStyle.tableCells.lowEfficiencyColor} onChange={(v) => updateStyleField('tableCells', 'lowEfficiencyColor', v)} />
            </CollapsibleSection>

            <CollapsibleSection title="Summary Section" description="Bottom summary border, background, and fonts">
              <ColorInput label="Border Color" value={reportStyle.summary.borderColor} onChange={(v) => updateStyleField('summary', 'borderColor', v)} />
              <ColorInput label="Title Background" value={reportStyle.summary.titleBackground} onChange={(v) => updateStyleField('summary', 'titleBackground', v)} />
              <SizeInput label="Title Font Size" value={reportStyle.summary.titleFontSize} onChange={(v) => updateStyleField('summary', 'titleFontSize', v)} />
              <SizeInput label="Content Font Size" value={reportStyle.summary.contentFontSize} onChange={(v) => updateStyleField('summary', 'contentFontSize', v)} />
              <SizeInput label="Value Font Size" value={reportStyle.summary.valueFontSize} onChange={(v) => updateStyleField('summary', 'valueFontSize', v)} />
            </CollapsibleSection>

            <CollapsibleSection title="Stop Row Colors" description="Row highlight colors based on machine stop duration">
              <div className="space-y-2">
                {reportStyle.stopRowColors.map((entry, index) => (
                  <div key={index} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div
                      className="w-6 h-6 rounded border border-gray-300 flex-shrink-0"
                      style={{ backgroundColor: entry.color }}
                    />
                    <input
                      type="color"
                      value={entry.color.startsWith('rgb') ? rgbToHex(entry.color) : entry.color}
                      onChange={(e) => updateStopRowColor(index, 'color', e.target.value)}
                      className="w-6 h-6 rounded cursor-pointer border-0 flex-shrink-0"
                    />
                    <input
                      type="text"
                      value={entry.color}
                      onChange={(e) => updateStopRowColor(index, 'color', e.target.value)}
                      className="w-24 px-2 py-1 text-xs border border-gray-300 rounded-md"
                    />
                    <div className="flex items-center gap-1">
                      <label className="text-xs text-gray-500">Min Hours:</label>
                      <input
                        type="number"
                        value={entry.minHours}
                        onChange={(e) => updateStopRowColor(index, 'minHours', e.target.value)}
                        className="w-16 px-2 py-1 text-xs border border-gray-300 rounded-md text-center"
                        min={0}
                        step={0.5}
                      />
                    </div>
                    <input
                      type="text"
                      value={entry.label}
                      onChange={(e) => updateStopRowColor(index, 'label', e.target.value)}
                      className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded-md"
                      placeholder="Label (e.g. 4+ hours)"
                    />
                    <button
                      onClick={() => removeStopRowColor(index)}
                      className="p-1 text-red-500 hover:text-red-700 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={addStopRowColor}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Add Stop Color
                </button>
              </div>
            </CollapsibleSection>

            <CollapsibleSection title="Report Columns" description="Choose which columns appear in the PDF report" defaultOpen>
              <p className="text-xs text-gray-500 mb-2">Uncheck columns to hide them from generated reports. Machine # is always visible.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {reportStyle.visibleColumns.map((col) => (
                  <label
                    key={col.key}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer select-none ${
                      col.visible ? 'bg-blue-50 border-blue-200' : 'bg-gray-50 border-gray-200'
                    } ${col.key === 'machineNo' ? 'opacity-60 cursor-not-allowed' : 'hover:bg-blue-50'}`}
                  >
                    <input
                      type="checkbox"
                      checked={col.visible}
                      disabled={col.key === 'machineNo'}
                      onChange={() => toggleColumnVisibility(col.key)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-800">{col.label}</span>
                  </label>
                ))}
              </div>
            </CollapsibleSection>
          </div>
        </div>

        {/* Report Preview */}
        <div className="bg-white shadow rounded-lg overflow-hidden mt-4">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Report Preview</h2>
            <p className="text-sm text-gray-500">Live preview of the PDF report with current settings. Actual PDF may vary slightly.</p>
          </div>
          <div className="p-4">
            <div style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: '8px', background: '#fff', overflow: 'hidden' }}>
              <iframe
                srcDoc={previewHtml}
                style={{ width: '100%', height: '380px', border: 'none', display: 'block' }}
                title="Report Preview"
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Billing Formula (company-wide challan/bill amount rule) */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Billing Formula</h2>
          <p className="text-sm text-gray-500">
            How each taka's amount is calculated on Challans &amp; Bills — applies to the whole company.
            Variables: <code className="text-gray-700">rate</code>, <code className="text-gray-700">meter</code>, <code className="text-gray-700">avgPick</code>, <code className="text-gray-700">piece</code>.
          </p>
        </div>
        <div className="px-6 py-4 space-y-3">
          {BILLING_PRESETS.map((p) => (
            <label key={p.formula} className="flex items-center gap-3 cursor-pointer">
              <input
                type="radio"
                name="billingPreset"
                checked={!billingCustom && billingFormula === p.formula}
                onChange={() => { setBillingCustom(false); setBillingFormula(p.formula); }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-800">{p.label}</span>
              <code className="text-xs text-gray-400 hidden sm:inline">{p.formula}</code>
            </label>
          ))}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="radio"
              name="billingPreset"
              checked={billingCustom}
              onChange={() => setBillingCustom(true)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-800">Custom formula</span>
          </label>
          {billingCustom && (
            <input
              type="text"
              value={billingFormula}
              onChange={(e) => setBillingFormula(e.target.value)}
              placeholder="e.g. rate * meter * 1.05"
              className={`block w-full max-w-md rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 ${billingError ? 'border border-red-300 focus:ring-red-500/30 focus:border-red-500' : 'border border-gray-300 focus:ring-blue-500/30 focus:border-blue-500'}`}
            />
          )}
          {billingError ? (
            <p className="text-sm text-red-600 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {billingError}</p>
          ) : (
            <p className="text-sm text-gray-500">
              Preview — sample rate 10, meter 100, avgPick 48, piece 1 →{' '}
              <span className="font-semibold text-gray-800">amount = {billingPreview.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </p>
          )}
          <div className="pt-1">
            <button
              onClick={() => { if (!billingError) saveBillingMutation.mutate(billingFormula.trim()); }}
              disabled={saveBillingMutation.isPending || !!billingError}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {saveBillingMutation.isPending ? 'Saving...' : 'Save Billing Formula'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
